import { Editor } from '@tinymce/tinymce-react';
import './style.scss';
import { marked } from "marked";
import { uploadFile } from '../../services/notes.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
const WAYMORE_CDN_DOMAIN = 'cdn12.waymore.io';

function convertToDisplayLink(url: string): string {
  const regex = /^(https:\/\/[^/]+)\/s\/([^/]+)/;
  const match = url.match(regex);

  if (match) {
    const domain = match[1];
    const id = match[2];
    return `${domain}/apps/raw/s/${id}`;
  }

  return url;
}

const TinyMCEEditor = ({ handleOnChange, editorValue, editorRef, onClick = () => { }, onFocus, onBlur, folderName = 'default' }: any) => {
  const userData: any = useSelector((state: RootState) => state.user?.user);
  const userId: any = userData?.userDetails?.id || "";

  const convertToCdnUrl = async (url: string) => {
    const progress = (percent: string) => { };
    const imageCdnUrl = await imageUploadHandler(undefined, progress, url);
    return imageCdnUrl;
  };

  const imageUploadHandler = (blobInfo: any, progress: any, imageUrl?: string): Promise<string> => new Promise(async (resolve, reject) => {
    try {
      const formData = new FormData();
      if (imageUrl) {
        formData.append('imageUrl', imageUrl);
      } else {
        formData.append('file', blobInfo?.blob(), blobInfo?.filename());
      }
      formData.append("isViewable", "true");
      await progress(50);
      if (!userId) {
        reject('User id not found!');
        return;
      }
      const cdnImageUrl = await uploadFile(userId, folderName, formData);
      if (!cdnImageUrl?.data || typeof cdnImageUrl?.data?.cdnURL !== 'string') {
        reject('Something went wrong, while image upload!');
        return;
      }
      const cdnURL = convertToDisplayLink(cdnImageUrl?.data?.cdnURL);
      resolve(cdnURL);
    } catch (error: any) {
      console.error("Error in imageUploadHandler", error);
      reject(error?.message);
    }
  });

  const handlePaste = async (editor: any, args: { node: HTMLElement }) => {
    setTimeout(async () => {
      const htmlContent = editor.getDoc();
      const content: HTMLElement = args.node;

      const urlRegex = /\bhttps?:\/\/[^\s<>"']+/g;

      // Regex for detecting valid email addresses
      const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;

      // Replace detected URLs and emails with anchor links
      content.innerHTML = content.innerHTML
        .replace(urlRegex, (match) => `<a href="${match}" target="_blank">${match}</a>`)
        .replace(emailRegex, (match) => `<a href="mailto:${match}">${match}</a>`);

      const images: any = content.getElementsByTagName('img');
      const cdnDomain = WAYMORE_CDN_DOMAIN;

      for (const img of images) {
        const isBlobUrl = img.src.startsWith('blob:');

        if (!isBlobUrl) {
          const isCdnUrl: boolean = img.src.includes(cdnDomain);
          const imgElements = htmlContent.querySelectorAll(`img[src="${img.src}"]`);

          if (img.src && !isCdnUrl && imgElements?.length) {
            const notification = editorRef.current?.notificationManager.open({
              text: 'Uploading image...',
              type: 'info',
              timeout: 0 // Keep loader until manually closed
            });

            try {
              const cdnUrl = await convertToCdnUrl(img.src);
              img.src = cdnUrl;

              for (const mainImgElement of imgElements) {
                mainImgElement.parentNode.replaceChild(img.cloneNode(true), mainImgElement);
              }
            } catch (error) {
              console.error('Error uploading image:', error);
            } finally {
              notification?.close();
            }
          }
        }
      }
    }, 10);
  };

  const editorHandlePaste = async (editor: any, event: any) => {
    const clipboardData = event.clipboardData || window.clipboardData;
    const items = Array.from(clipboardData.items);
    let textData = clipboardData.getData('text/plain').trim();
    const htmlData = clipboardData.getData('text/html');
    const hasImage = items.some((item: any) => item.type.startsWith('image'));

    if (hasImage || htmlData) {
      return; // Let TinyMCE handle images and raw HTML pasting
    }

    if (textData) {
      event.preventDefault();
      let convertedHtml = marked.parse(textData);
      editor.insertContent(convertedHtml);
    }
  };

  return (
    <div className='tinymce-component' style={{ paddingBottom: "10px", outline: 0 }}>
      <Editor
        onInit={(_, editor) => (editorRef.current = editor)}
        licenseKey="gpl"
        init={{
          height: 200,
          menubar: false,
          plugins: ['link', 'paste', 'lists', 'code', 'codesample', 'anchor', 'autolink', 'table', 'visualblocks', 'image', 'media', 'markdown'],
          toolbar: 'blocks bold italic strikethrough codesample link bullist numlist table image',
          link_assume_external_targets: false, // Prevents accidental word linking
          paste_data_images: true, // Enable pasting images
          images_upload_credentials: true,
          setup: (editor) => {
            editor.on('click', (e) => {
              let target = e.target;
              if (target.tagName === 'A') {
                window.open(target.href, '_blank');
              }
            });
            editor.on('paste', (event) => {
              editorHandlePaste(editor, event);
            });
          },
          content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:16px }',
          font_size_formats: '8pt 10pt 12pt 14pt 18pt 24pt 36pt',
          block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3;Heading 4=h4;Heading 5=h5; Heading 6=h6;',
          placeholder: 'Add description here...',
          codesample_global_prismjs: true,
          codesample_languages: [
            { text: 'HTML/XML', value: 'markup' },
            { text: 'JavaScript', value: 'javascript' }],
          branding: false,
          valid_elements: '*[*]', // Allow all elements in the editor
          paste_as_text: false, // Prevent pasting formatted content that could introduce <p> tags
          block_unsupported_attrs: true,
          resize: true,
          elementpath: false,  // Removes the element path at the bottom (which shows <p> tags)
          statusbar: true,
          // Added options to remove <p> tags and tooltips
          forced_root_block: '',  // Disable auto <p> tags
          contextmenu: false,        // Disable right-click context menu (tooltips)
          images_upload_handler: imageUploadHandler,
          paste_postprocess: handlePaste,
        }}
        onEditorChange={handleOnChange}
        value={editorValue}
        onClick={onClick}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </div>
  );
};

export default TinyMCEEditor;